import { useEffect, useState } from 'react';
import type { AgentSettings } from '@supporchat/shared';
import { api } from '../lib/api';
import { ProfilePhotoCard } from '../components/ProfilePhotoCard';
import { SignatureCard } from '../components/SignatureCard';

/** Espelha o limite validado pela API (`UpdateAgentSettingsDto.systemPrompt`). */
const PROMPT_MAX_LENGTH = 40_000;

export function AgentPage({
  isAdmin,
  userId,
  userName,
  avatarVersion,
  onAvatarChange,
}: {
  isAdmin: boolean;
  userId: string;
  userName: string;
  avatarVersion: string | null;
  onAvatarChange: (version: string | null) => void;
}) {
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [draft, setDraft] = useState<AgentSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const current = await api.getAgentSettings();
        setSettings(current);
        setDraft(current);
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, []);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api.updateAgentSettings(draft);
      setSettings(updated);
      setDraft(updated);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!draft || !settings) {
    return (
      <div className="page">
        <div className="page__inner">
          {error ? <div className="alert alert--error">{error}</div> : <div className="empty">Carregando...</div>}
        </div>
      </div>
    );
  }

  const update = <K extends keyof AgentSettings>(key: K, value: AgentSettings[K]) =>
    setDraft({ ...draft, [key]: value });

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  return (
    <div className="page">
      <div className="page__inner">
        {error && <div className="alert alert--error">{error}</div>}
        {saved && !dirty && <div className="alert alert--success">Configurações salvas.</div>}
        {!isAdmin && (
          <div className="alert alert--info">
            Apenas administradores podem alterar estas configurações.
          </div>
        )}

        <div className="card">
          <h3 className="card__title">Resposta automática</h3>
          <p className="card__subtitle">
            Com o agente ligado, toda mensagem de cliente em conversa não assumida por atendente é
            respondida automaticamente — se houver apoio na base de conhecimento.
          </p>

          <div className="checkbox">
            <input
              id="agent-enabled"
              type="checkbox"
              checked={draft.enabled}
              disabled={!isAdmin}
              onChange={(event) => update('enabled', event.target.checked)}
            />
            <label htmlFor="agent-enabled">
              Agente ativo
              <div className="field__hint">
                Desligado, todas as conversas ficam esperando atendimento humano.
              </div>
            </label>
          </div>

          <div className="checkbox">
            <input
              id="agent-only-kb"
              type="checkbox"
              checked={draft.answerOnlyFromKnowledge}
              disabled={!isAdmin}
              onChange={(event) => update('answerOnlyFromKnowledge', event.target.checked)}
            />
            <label htmlFor="agent-only-kb">
              Responder somente com base no conhecimento cadastrado
              <div className="field__hint">
                Recomendado. Sem isso o modelo pode responder por conhecimento geral e inventar
                informação sobre a sua empresa.
              </div>
            </label>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="agent-model">Modelo</label>
              <input
                id="agent-model"
                value={draft.model}
                disabled={!isAdmin}
                onChange={(event) => update('model', event.target.value)}
              />
              <span className="field__hint">Modelo de chat da OpenAI usado nas respostas.</span>
            </div>

            <div className="field">
              <label htmlFor="agent-confidence">Confiança mínima ({draft.minConfidence.toFixed(2)})</label>
              <input
                id="agent-confidence"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={draft.minConfidence}
                disabled={!isAdmin}
                onChange={(event) => update('minConfidence', Number(event.target.value))}
              />
              <span className="field__hint">Abaixo disso, transfere em vez de responder.</span>
            </div>

            <div className="field">
              <label htmlFor="agent-temperature">Temperatura ({draft.temperature.toFixed(2)})</label>
              <input
                id="agent-temperature"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={draft.temperature}
                disabled={!isAdmin}
                onChange={(event) => update('temperature', Number(event.target.value))}
              />
              <span className="field__hint">Baixa mantém as respostas previsíveis.</span>
            </div>

            <div className="field">
              <label htmlFor="agent-topk">Itens da base por resposta</label>
              <input
                id="agent-topk"
                type="number"
                min={1}
                max={20}
                value={draft.ragTopK}
                disabled={!isAdmin}
                onChange={(event) => update('ragTopK', Number(event.target.value))}
              />
            </div>

            <div className="field">
              <label htmlFor="agent-context">Mensagens de contexto</label>
              <input
                id="agent-context"
                type="number"
                min={2}
                max={50}
                value={draft.maxContextMessages}
                disabled={!isAdmin}
                onChange={(event) => update('maxContextMessages', Number(event.target.value))}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="card__title">Comportamento e tom</h3>

          <div className="field">
            <label htmlFor="agent-prompt">Instruções do agente</label>
            <textarea
              id="agent-prompt"
              value={draft.systemPrompt}
              disabled={!isAdmin}
              style={{ minHeight: 190, fontFamily: 'var(--mono)', fontSize: 12 }}
              onChange={(event) => update('systemPrompt', event.target.value)}
            />
            <span className="field__hint">
              Descreva tom de voz, o que pode e o que não pode prometer. As regras de transferência e o
              formato de resposta são aplicados pelo sistema e não podem ser desativados aqui.
              {' '}
              {/* O texto vai em toda chamada ao modelo, então o tamanho aparece: é
                  o que o admin paga por resposta, não um detalhe de formulário. */}
              <strong>
                {draft.systemPrompt.length.toLocaleString('pt-BR')} de{' '}
                {PROMPT_MAX_LENGTH.toLocaleString('pt-BR')} caracteres
              </strong>
              , enviados em cada resposta do agente.
            </span>
          </div>

          <div className="field">
            <label htmlFor="agent-keywords">Palavras que transferem direto para um atendente</label>
            <input
              id="agent-keywords"
              value={draft.handoffKeywords.join(', ')}
              disabled={!isAdmin}
              onChange={(event) =>
                update(
                  'handoffKeywords',
                  event.target.value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean),
                )
              }
            />
            <span className="field__hint">
              Separadas por vírgula. Comparadas sem acento e sem diferenciar maiúsculas.
            </span>
          </div>

          <div className="field">
            <label htmlFor="agent-fallback">Mensagem quando a base não cobre a dúvida</label>
            <input
              id="agent-fallback"
              value={draft.fallbackMessage}
              disabled={!isAdmin}
              onChange={(event) => update('fallbackMessage', event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="agent-escalation">Mensagem ao transferir para atendente</label>
            <input
              id="agent-escalation"
              value={draft.escalationMessage}
              disabled={!isAdmin}
              onChange={(event) => update('escalationMessage', event.target.value)}
            />
          </div>

          {/* Cortesia não passa pela base de conhecimento: quem cumprimenta não fez
              uma pergunta, e antes disso a conversa era transferida para uma pessoa
              antes de o cliente dizer o que queria. */}
          <div className="field">
            <label htmlFor="agent-greeting">Resposta a quem só cumprimenta</label>
            <input
              id="agent-greeting"
              value={draft.greetingMessage}
              disabled={!isAdmin}
              onChange={(event) => update('greetingMessage', event.target.value)}
            />
            <div className="field__hint">
              Usada quando a mensagem é só "bom dia", "oi", "tudo bem?" — sem pedido. Peça o problema aqui:
              é a deixa que o cliente está esperando.
            </div>
          </div>

          <div className="field">
            <label htmlFor="agent-farewell">Resposta a agradecimento e despedida</label>
            <input
              id="agent-farewell"
              value={draft.farewellMessage}
              disabled={!isAdmin}
              onChange={(event) => update('farewellMessage', event.target.value)}
            />
            <div className="field__hint">
              Para "obrigado", "valeu", "tchau". Depois de três mensagens sem um pedido, o agente chama um
              atendente.
            </div>
          </div>

          <div className="btn-row">
            <button className="btn btn--primary" disabled={!isAdmin || saving || !dirty} onClick={() => void save()}>
              {saving ? 'Salvando' : 'Salvar configurações'}
            </button>
            {dirty && (
              <button className="btn" onClick={() => setDraft(settings)}>
                Descartar alterações
              </button>
            )}
          </div>
        </div>

        <SignatureCard isAdmin={isAdmin} userName={userName} />

        <ProfilePhotoCard
          userId={userId}
          userName={userName}
          version={avatarVersion}
          onChange={onAvatarChange}
        />
      </div>
    </div>
  );
}
